import {
  ALLERGENS,
  ATTRIBUTE_GROUPS,
  allergenLabel,
  allergenEmoji,
  attributeLabel,
  attributeColor,
} from '../lib/allergen-attribute-config'

export interface AllergenAttributeValue {
  allergens: string[]
  attributes: string[]
}

interface AllergenAttributeEditorProps {
  value: AllergenAttributeValue
  onChange?: (next: AllergenAttributeValue) => void
  /** Render coloured badges instead of interactive pickers. */
  readOnly?: boolean
  /** Disable inputs (e.g. no `product.update` permission). Still shows current selection. */
  disabled?: boolean
}

/**
 * Structured picker for the fixed EU-14 allergens and the fixed dish-attribute
 * enum. Controlled component: the parent owns `{ allergens, attributes }` and is
 * responsible for wrapping it into `allergen_info_json` / `attributes_json` on save
 * (always send the full arrays — the API overwrites, it does not merge).
 */
const AllergenAttributeEditor = ({
  value,
  onChange,
  readOnly = false,
  disabled = false,
}: AllergenAttributeEditorProps) => {
  const allergens = value.allergens ?? []
  const attributes = value.attributes ?? []

  const toggleAllergen = (code: string) => {
    if (!onChange) return
    const next = allergens.includes(code)
      ? allergens.filter((a) => a !== code)
      : [...allergens, code]
    onChange({ allergens: next, attributes })
  }

  const toggleAttribute = (code: string, singleSelect: boolean, groupCodes: string[]) => {
    if (!onChange) return
    let next: string[]
    if (attributes.includes(code)) {
      next = attributes.filter((a) => a !== code)
    } else if (singleSelect) {
      // Only one badge from a single-select group (spice) may be active.
      next = [...attributes.filter((a) => !groupCodes.includes(a)), code]
    } else {
      next = [...attributes, code]
    }
    onChange({ allergens, attributes: next })
  }

  // --- Read-only badge view (product detail page) --------------------------
  if (readOnly) {
    const hasAny = allergens.length > 0 || attributes.length > 0
    if (!hasAny) {
      return <p className="text-sm text-gray-500">No allergens or attributes set.</p>
    }
    return (
      <div className="space-y-3">
        {attributes.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attributes.map((code) => (
              <span
                key={code}
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${attributeColor(code)}`}
              >
                {attributeLabel(code)}
              </span>
            ))}
          </div>
        )}
        {allergens.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {allergens.map((code) => (
              <span
                key={code}
                className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700"
              >
                <span className="mr-1">{allergenEmoji(code)}</span>
                {allergenLabel(code)}
              </span>
            ))}
          </div>
        )}
      </div>
    )
  }

  // --- Interactive picker -------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Attributes */}
      <div className="space-y-4">
        {ATTRIBUTE_GROUPS.map((group) => (
          <div key={group.key}>
            <p className="text-sm font-medium text-gray-700 mb-2">
              {group.title}
              {group.singleSelect && (
                <span className="ml-1 text-xs font-normal text-gray-400">(pick one)</span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {group.codes.map((code) => {
                const selected = attributes.includes(code)
                return (
                  <button
                    key={code}
                    type="button"
                    disabled={disabled}
                    onClick={() => toggleAttribute(code, !!group.singleSelect, group.codes)}
                    className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      selected
                        ? `${attributeColor(code)} border-transparent ring-2 ring-offset-1 ring-primary-400`
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {attributeLabel(code)}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Allergens */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Allergens (EU-14)</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {ALLERGENS.map((code) => {
            const checked = allergens.includes(code)
            return (
              <label
                key={code}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${
                  disabled ? 'opacity-50 cursor-not-allowed' : ''
                } ${
                  checked
                    ? 'border-primary-400 bg-primary-50 text-primary-900'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggleAllergen(code)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="mr-0.5">{allergenEmoji(code)}</span>
                <span>{allergenLabel(code)}</span>
              </label>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default AllergenAttributeEditor
